import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import {
  escalationsResponseSchema,
  rulesResponseSchema,
  stageDeadlinesResponseSchema,
  type ActiveEscalations,
  type NotificationRule,
  type RuleCode,
  type RuleUpdate,
  type StageDeadlines,
} from './types';

// Configuración de avisos de la empresa activa (so=370, backend so=330) y escalamientos
// abiertos (panel so=410). La empresa va en la posición 1 del queryKey.

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

export const ESCALATIONS_SHOWN = 5;

export function useNotificationRules({
  companyId,
  enabled,
}: Scope): UseQueryResult<NotificationRule[]> {
  const api = useApi();
  return useQuery({
    queryKey: ['notification-rules', companyId],
    queryFn: async () =>
      rulesResponseSchema.parse(await api.get<unknown>('/api/v1/quality/notification-rules')).data,
    enabled,
  });
}

export function useUpdateRule(): UseMutationResult<
  unknown,
  Error,
  { code: RuleCode; body: RuleUpdate }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }) =>
      api.put<unknown>(`/api/v1/quality/notification-rules/${code}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

export function useStageDeadlines({ companyId, enabled }: Scope): UseQueryResult<StageDeadlines> {
  const api = useApi();
  return useQuery({
    queryKey: ['stage-deadlines', companyId],
    queryFn: async () =>
      stageDeadlinesResponseSchema.parse(await api.get<unknown>('/api/v1/quality/stage-deadlines'))
        .data,
    enabled,
  });
}

export function useUpdateStageDeadlines(): UseMutationResult<unknown, Error, StageDeadlines> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put<unknown>('/api/v1/quality/stage-deadlines', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stage-deadlines'] }),
  });
}

/** Etapas con escalamiento abierto ahora (las primeras ESCALATIONS_SHOWN y el total). */
export function useActiveEscalations({
  companyId,
  enabled,
}: Scope): UseQueryResult<ActiveEscalations> {
  const api = useApi();
  return useQuery({
    queryKey: ['active-escalations', companyId],
    queryFn: async () =>
      escalationsResponseSchema.parse(
        await api.get<unknown>(
          `/api/v1/quality/dashboard/deadlines?escalated=true&window=1&limit=${ESCALATIONS_SHOWN}`,
        ),
      ).data,
    enabled,
    // Reintentos: la política global (query-client) no repite un 403 (sin permiso del panel).
  });
}
